import { apiClient } from './client';

export interface LearnSession {
  session_id: string;
  project_id: string;
  topic: string;
  description?: string;
  status: 'active' | 'completed' | 'cancelled';
  memory_type_focus?: string;
  domain?: string;
  created_at: string;
  updated_at?: string;
  message_count?: number;
  extracted_memory_count?: number;
  confirmed_memory_count?: number;
}

export interface StartSessionRequest {
  topic: string;
  description?: string;
  memory_type_focus?: 'procedure' | 'fact' | 'concept';
  domain?: string;
}

export interface StartSessionResponse {
  session_id: string;
  initial_prompt: string;
  status: string;
}

export interface SendMessageRequest {
  content: string;
}

export interface ExtractedMemory {
  id: string;
  memory_type: 'procedure' | 'fact' | 'concept' | 'preference';
  title: string;
  content: string;
  domain?: string;
  confidence: number;
  confirmed: boolean;
  rejected: boolean;
  properties?: Record<string, any>;
}

export interface SendMessageResponse {
  response: string;
  extracted_memories?: ExtractedMemory[];
  suggested_questions?: string[];
}

export interface FinalizeSessionResponse {
  status: string;
  committed_memories: string[];
  memory_count: number;
}

export interface Memory {
  id: string;
  project_id: string;
  memory_type: 'procedure' | 'fact' | 'concept' | 'preference';
  title: string;
  content: string;
  domain?: string;
  source_session_id?: string;
  confidence: number;
  status: 'active' | 'invalidated';
  properties?: Record<string, any>;
  created_at: string;
  updated_at?: string;
}

export interface ListMemoriesRequest {
  limit?: number;
  offset?: number;
  memory_type?: string;
  domain?: string;
  search?: string;
  status?: string;
}

export interface ListMemoriesResponse {
  memories: Memory[];
  total: number;
}

export interface UpdateMemoryRequest {
  title?: string;
  content?: string;
  domain?: string;
  properties?: Record<string, any>;
}

export interface SkillParameter {
  name: string;
  type: string;
  description: string;
  required: boolean;
  default_value?: any;
}

export interface SkillStep {
  order: number;
  action: string;
  description: string;
}

export interface Skill {
  id: string;
  project_id: string;
  name: string;
  description: string;
  source_memory_id?: string;
  source_session_id?: string;
  parameters: SkillParameter[];
  steps: SkillStep[];
  status: 'active' | 'disabled';
  created_at: string;
  updated_at?: string;
  execution_count?: number;
}

export interface ListSkillsRequest {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
}

export interface ListSkillsResponse {
  skills: Skill[];
  total: number;
}

export interface ExecuteSkillRequest {
  parameters: Record<string, any>;
}

export interface SkillExecutionStep {
  order: number;
  action: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: string;
  error?: string;
}

export interface SkillExecutionResponse {
  execution_id: string;
  skill_id: string;
  status: 'running' | 'completed' | 'failed';
  steps: SkillExecutionStep[];
  result?: any;
  error?: string;
}

export interface ListSessionsResponse {
  sessions: LearnSession[];
  total: number;
}

// Agent (DO-mode) session types
export interface AgentSession {
  session_id: string;
  project_id: string;
  topic: string;
  status: string;
  mode: string;
  message_count: number;
  created_at: string;
  last_active: string;
}

export interface ListAgentSessionsResponse {
  sessions: AgentSession[];
  total: number;
}

export const agentService = {
  async listSessions(projectId: string): Promise<ListAgentSessionsResponse> {
    return apiClient.get<ListAgentSessionsResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/agent/sessions`
    );
  },
};

export const ltmemoryService = {
  async listSessions(projectId: string, limit = 20, offset = 0): Promise<ListSessionsResponse> {
    return apiClient.get<ListSessionsResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions?limit=${limit}&offset=${offset}`
    );
  },

  async getSession(projectId: string, sessionId: string): Promise<LearnSession> {
    return apiClient.get<LearnSession>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}`
    );
  },

  async startSession(projectId: string, request: StartSessionRequest): Promise<StartSessionResponse> {
    return apiClient.post<StartSessionResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions`,
      request
    );
  },

  async sendMessage(projectId: string, sessionId: string, request: SendMessageRequest): Promise<SendMessageResponse> {
    return apiClient.post<SendMessageResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}/message`,
      request
    );
  },

  async confirmMemory(projectId: string, sessionId: string, memoryId: string): Promise<void> {
    return apiClient.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}/memories/${encodeURIComponent(memoryId)}/confirm`
    );
  },

  async rejectMemory(projectId: string, sessionId: string, memoryId: string): Promise<void> {
    return apiClient.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}/memories/${encodeURIComponent(memoryId)}/reject`
    );
  },

  async finalizeSession(projectId: string, sessionId: string): Promise<FinalizeSessionResponse> {
    return apiClient.post<FinalizeSessionResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}/finalize`
    );
  },

  async cancelSession(projectId: string, sessionId: string): Promise<void> {
    return apiClient.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}/cancel`
    );
  },

  async deleteSession(projectId: string, sessionId: string): Promise<void> {
    return apiClient.delete(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/learn/sessions/${encodeURIComponent(sessionId)}`
    );
  },

  // Memory Browser APIs
  async listMemories(projectId: string, params: ListMemoriesRequest = {}): Promise<ListMemoriesResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    if (params.memory_type) queryParams.set('memory_type', params.memory_type);
    if (params.domain) queryParams.set('domain', params.domain);
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    
    const query = queryParams.toString();
    return apiClient.get<ListMemoriesResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/memories${query ? `?${query}` : ''}`
    );
  },

  async getMemory(projectId: string, memoryId: string): Promise<Memory> {
    return apiClient.get<Memory>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/memories/${encodeURIComponent(memoryId)}`
    );
  },

  async updateMemory(projectId: string, memoryId: string, request: UpdateMemoryRequest): Promise<Memory> {
    return apiClient.put<Memory>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/memories/${encodeURIComponent(memoryId)}`,
      request
    );
  },

  async deleteMemory(projectId: string, memoryId: string): Promise<void> {
    return apiClient.delete(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/memories/${encodeURIComponent(memoryId)}`
    );
  },

  async invalidateMemory(projectId: string, memoryId: string): Promise<void> {
    return apiClient.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/memories/${encodeURIComponent(memoryId)}/invalidate`
    );
  },

  // Skills APIs
  async listSkills(projectId: string, params: ListSkillsRequest = {}): Promise<ListSkillsResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.status) queryParams.set('status', params.status);
    
    const query = queryParams.toString();
    return apiClient.get<ListSkillsResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills${query ? `?${query}` : ''}`
    );
  },

  async getSkill(projectId: string, skillId: string): Promise<Skill> {
    return apiClient.get<Skill>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}`
    );
  },

  async executeSkill(projectId: string, skillId: string, request: ExecuteSkillRequest): Promise<SkillExecutionResponse> {
    return apiClient.post<SkillExecutionResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}/execute`,
      request
    );
  },

  async getSkillExecution(projectId: string, skillId: string, executionId: string): Promise<SkillExecutionResponse> {
    return apiClient.get<SkillExecutionResponse>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}/executions/${encodeURIComponent(executionId)}`
    );
  },

  async deleteSkill(projectId: string, skillId: string): Promise<void> {
    return apiClient.delete(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}`
    );
  },

  async updateSkill(projectId: string, skillId: string, data: { name?: string; description?: string; status?: string }): Promise<Skill> {
    return apiClient.put<Skill>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}`,
      data
    );
  },

  async disableSkill(projectId: string, skillId: string): Promise<void> {
    return apiClient.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/ltmemory/skills/${encodeURIComponent(skillId)}/disable`
    );
  },
};
